import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Calendar, Clock, Users, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuthContext } from '../App';

interface Session {
  id: number;
  name: string;
  description: string;
  session_date: string;
  start_time: string;
  end_time: string;
  gusmester_name: string;
  gusmester_email: string;
  max_capacity: number;
  booked_count: number;
  waiting_count: number;
  gusmester_id: number;
  session_type: 'regular' | 'special_event';
  gusmesters?: { id: number; name: string }[];
}

const Sessions: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joiningWaitingList, setJoiningWaitingList] = useState<number | null>(null);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchSessions();
  }, [currentMonth]);

  const fetchSessions = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = (currentMonth.getMonth() + 1).toString().padStart(2, '0');
      const response = await axios.get(`/api/sessions/month/${year}/${month}`);
      setSessions(response.data);
    } catch (error) {
      setError('Kunne ikke indlæse sessioner');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableSpots = (session: Session) => {
    return session.max_capacity - session.booked_count;
  };

  const isSessionFull = (session: Session) => {
    return session.booked_count >= session.max_capacity;
  };

  const getDayOfWeek = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'EEEE');
  };

  const isInSeason = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    // Season: September 1st (month 9) to May 1st (month 5)
    if (month >= 9) return true; // September to December
    if (month <= 5 && day <= 1) return true; // January 1st to May 1st
    return false;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => addMonths(prev, -1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handleJoinWaitingList = async (sessionId: number) => {
    setJoiningWaitingList(sessionId);
    try {
      await axios.post(`/api/waiting-list/${sessionId}`);
      // Refresh sessions to update waiting list count
      await fetchSessions();
      alert('Du er nu tilføjet til ventelisten!');
    } catch (error: any) {
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Kunne ikke tilslutte dig ventelisten');
      }
    } finally {
      setJoiningWaitingList(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Fejl</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchSessions}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg"
          >
            Prøv Igen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header with Icy Winter Background */}
      <section className="pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 text-white py-16">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-35"
              style={{ backgroundImage: "url('/winter/icy-bridge.jpg')" }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-blue-950/40 to-slate-950/70"></div>
            <div className="relative px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">GUS Sessioner</h1>
                <p className="text-xl md:text-2xl max-w-3xl mx-auto">
                  Book dine gus her. Sessioner afholdes ugentligt kl. 20:00-21:00.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handlePreviousMonth}
            className="flex items-center text-blue-700 hover:text-blue-800 font-medium"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Forrige Måned
          </button>
          
          <h2 className="text-2xl font-bold text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          
          <button
            onClick={handleNextMonth}
            className="flex items-center text-blue-700 hover:text-blue-800 font-medium"
          >
            Næste Måned
            <ChevronRight className="w-5 h-5 ml-1" />
          </button>
        </div>

        {/* Season Information */}
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-sky-700 mr-2" />
            <div>
              <h3 className="font-semibold text-sky-800">Sæson Information</h3>
              <p className="text-sky-700 text-sm">
                GUS sessioner kører fra 1. september til 1. maj. Sessioner afholdes ugentligt kl. 20:00-21:00.
              </p>
            </div>
          </div>
        </div>

        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Ingen sessioner planlagt</h3>
            <p className="text-gray-600 mb-4">
              {!isInSeason(format(currentMonth, 'yyyy-MM-dd')) 
                ? "Denne måned er uden for sauna sæsonen (1. september til 1. maj)."
                : "Ingen GUS sessioner er endnu planlagt for denne måned."
              }
            </p>
            {user?.isAdmin && (
              <Link
                to="/admin"
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Planlæg Sessioner
              </Link>
            )}
          </div>
        ) : (
          <div className="grid gap-6">
            {sessions.map((session) => (
              <div key={session.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {session.name || `GUS Session - ${getDayOfWeek(session.session_date)}`}
                        </h3>
                        <p className="text-gray-600">
                          {format(new Date(session.session_date), 'EEEE, MMMM d, yyyy')}
                        </p>
                        {session.description && (
                          <p className="text-gray-700 mt-1 whitespace-pre-wrap">{session.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Kapacitet</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {session.booked_count}/{session.max_capacity}
                        </div>
                        {session.waiting_count > 0 && (
                          <div className="text-xs text-sky-700">
                            {session.waiting_count} på venteliste
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>{session.start_time} - {session.end_time}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        {session.session_type === 'special_event' && session.gusmesters && session.gusmesters.length > 0 ? (
                          <span>
                            {session.gusmesters.map((g, i) => (
                              <React.Fragment key={g.id}>
                                {i > 0 && (i === session.gusmesters!.length - 1 ? ' og ' : ', ')}
                                <Link to={`/gusmasters/${g.id}`}>{g.name}</Link>
                              </React.Fragment>
                            ))}
                          </span>
                        ) : session.gusmester_name ? (
                          <Link to={`/gusmasters/${session.gusmester_id}`}>{session.gusmester_name}</Link>
                        ) : (
                          <span className="text-gray-500">Ingen Gusmester</span>
                        )}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{getAvailableSpots(session)} pladser ledige</span>
                      </div>
                    </div>


                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {isSessionFull(session) ? (
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        Fuldt
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        Ledig
                      </span>
                    )}
                    
                    {getAvailableSpots(session) <= 3 && getAvailableSpots(session) > 0 && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        Få pladser tilbage
                      </span>
                    )}
                  </div>

                  {user ? (
                    isSessionFull(session) ? (
                      <div className="flex flex-col space-y-2">
                        <span className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed">
                          Fuldt
                        </span>
                        <button
                          onClick={() => handleJoinWaitingList(session.id)}
                          disabled={joiningWaitingList === session.id}
                          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                          {joiningWaitingList === session.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            'Tilslut Venteliste'
                          )}
                        </button>
                      </div>
                    ) : (
                      <Link
                        to={`/booking/${session.id}`}
                        className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                      >
                        Book Session
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    )
                  ) : (
                    <Link
                      to="/login"
                      className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Log Ind for at Booke
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booking Information */}
        <div className="mt-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Information</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><strong>Kapacitet:</strong> Maksimum 16 personer per session</p>
              <p><strong>Månedlig Grænse:</strong> 2 sessioner per måned per medlem</p>
              <p><strong>Tidsplan:</strong> Ugentlige sessioner kl. 20:00-21:00</p>
            </div>
            <div>
              <p><strong>Sæson:</strong> 1. september til 1. maj</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sessions; 